import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckSquare, FiPlus, FiSquare, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const BULK_NO_CHANGE = "__no_change__";

const PAYMENT_CATEGORY_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "credit_card", label: "Credit Card" },
  { value: "room_charge", label: "Room Charge" },
  { value: "other", label: "Others" },
];

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function categoryLabel(value) {
  return PAYMENT_CATEGORY_OPTIONS.find((option) => option.value === value)?.label || "Others";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export default function PaymentMethods() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [methods, setMethods] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [allFilteredSelected, setAllFilteredSelected] = useState(false);
  const [bulkCategory, setBulkCategory] = useState(BULK_NO_CHANGE);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMethod, setNewMethod] = useState({ name: "", category: "other" });
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const limit = 50;

  const hasPrevious = offset > 0;
  const hasNext = offset + limit < count;
  const visibleMethodIds = useMemo(() => methods.map((method) => method.id), [methods]);
  const selectedCount = allFilteredSelected ? count : selectedIds.size;
  const selectionLabel = allFilteredSelected ? `${count} filtered selected` : `${selectedIds.size} selected`;

  const loadMethods = useCallback(async (nextOffset = 0) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/payment-methods", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedSearchQuery,
          category: categoryFilter,
          limit,
          offset: nextOffset,
        },
      });
      setMethods(res.data?.rows || []);
      setCount(res.data?.count || 0);
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load POS payment methods.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, categoryFilter, debouncedSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedIds(new Set());
    setAllFilteredSelected(false);
  }, [businessUnitId, debouncedSearchQuery, categoryFilter]);

  useEffect(() => {
    loadMethods(0);
  }, [loadMethods]);

  const clearSelection = () => {
    setSelectedIds(new Set());
    setAllFilteredSelected(false);
  };

  const selectVisible = () => {
    setSelectedIds(new Set(visibleMethodIds));
    setAllFilteredSelected(false);
  };

  const selectAllFiltered = () => {
    if (!count) return;
    setSelectedIds(new Set());
    setAllFilteredSelected(true);
  };

  const toggleVisibleSelection = () => {
    if (allFilteredSelected) {
      clearSelection();
      return;
    }
    const allVisibleSelected = visibleMethodIds.length > 0 && visibleMethodIds.every((id) => selectedIds.has(id));
    if (allVisibleSelected) {
      setSelectedIds((current) => {
        const next = new Set(current);
        visibleMethodIds.forEach((id) => next.delete(id));
        return next;
      });
      return;
    }
    setSelectedIds((current) => {
      const next = new Set(current);
      visibleMethodIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleMethodSelection = (methodId) => {
    if (allFilteredSelected) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(methodId)) next.delete(methodId);
      else next.add(methodId);
      return next;
    });
  };

  const updateMethod = async (method, patch) => {
    try {
      setSavingId(method.id);
      setError("");
      setMessage("");
      const res = await axios.patch(`/api/pos/payment-methods/${method.id}`, {
        ...patch,
        business_unit_id: businessUnitId,
      });
      setMethods((current) => current.map((row) => (row.id === method.id ? res.data : row)));
      await loadMethods(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update POS payment method.");
    } finally {
      setSavingId(null);
    }
  };

  const applyBulkEdit = async () => {
    if (!selectedCount) {
      setError("Select payment methods before applying a bulk edit.");
      return;
    }
    if (bulkCategory === BULK_NO_CHANGE) {
      setError("Choose a category to update.");
      return;
    }

    try {
      setBulkSaving(true);
      setError("");
      setMessage("");
      const res = await axios.patch("/api/pos/payment-methods/bulk", {
        business_unit_id: businessUnitId,
        scope: allFilteredSelected ? "filtered" : "ids",
        payment_method_ids: allFilteredSelected ? undefined : Array.from(selectedIds),
        filters: {
          q: debouncedSearchQuery,
          category: categoryFilter,
        },
        patch: {
          category: bulkCategory,
        },
      });
      setMessage(`${Number(res.data?.updated_count || 0)} payment methods updated.`);
      setBulkCategory(BULK_NO_CHANGE);
      clearSelection();
      await loadMethods(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update POS payment methods.");
    } finally {
      setBulkSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setCategoryFilter("");
  };

  const createMethod = async () => {
    const name = newMethod.name.trim();
    if (!name) {
      setError("Payment method name is required.");
      return;
    }

    try {
      setCreating(true);
      setError("");
      setMessage("");
      await axios.post("/api/pos/payment-methods", {
        business_unit_id: businessUnitId,
        name,
        category: newMethod.category || "other",
      });
      setMessage(`${name} created.`);
      setNewMethod({ name: "", category: "other" });
      setSearchQuery("");
      setDebouncedSearchQuery("");
      setCategoryFilter("");
      await loadMethods(0);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not create POS payment method.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="reservations-container rm-page">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Payment methods</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading payment methods..." : `${count} methods in the POS catalog`}
            </div>
          </div>
          <div className="rm-topActions">
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search payment method"
            />
            <select
              className="posSelect posPaymentToolbarSelect"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="">All categories</option>
              {PAYMENT_CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={clearFilters}>
              <FiX /> Clear
            </button>
            <button
              type="button"
              className={`rm-topBtn ${showCreateForm ? "rm-topBtn--green" : ""}`}
              onClick={() => setShowCreateForm((prev) => !prev)}
            >
              <FiPlus /> Add method
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      {showCreateForm ? (
        <div className="rm-rates-card posClassificationCard">
          <div className="rm-rates-header">
            <h3 className="rm-rates-title">Add payment method</h3>
            <div className="rm-rates-subtitle">Create tender types before using them in tickets.</div>
          </div>
          <div className="posTaxonomyForm posTaxonomyForm--inline">
            <input
              className="posTextInput"
              value={newMethod.name}
              onChange={(event) => setNewMethod((prev) => ({ ...prev, name: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === "Enter") createMethod();
              }}
              placeholder="Method name"
            />
            <select
              className="posSelect"
              value={newMethod.category}
              onChange={(event) => setNewMethod((prev) => ({ ...prev, category: event.target.value }))}
            >
              {PAYMENT_CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={createMethod} disabled={creating}>
              <FiPlus /> {creating ? "Creating" : "Create"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="rm-rates-card posBulkCard">
        <div className="posBulkSelectionRow">
          <div className="posBulkStatus">
            <strong>{selectionLabel}</strong>
            <span>{allFilteredSelected ? "Matching current filters" : "Manual selection"}</span>
          </div>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={selectVisible} disabled={!methods.length}>
              <FiCheckSquare /> Visible
            </button>
            <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={selectAllFiltered} disabled={!count}>
              <FiCheckSquare /> All filtered
            </button>
            <button type="button" className="rm-topBtn" onClick={clearSelection} disabled={!selectedCount}>
              <FiX /> Clear
            </button>
          </div>
        </div>

        <div className="posBulkGrid posPaymentBulkGrid">
          <label className="posBulkField">
            <span>Category</span>
            <select
              className="posSelect"
              value={bulkCategory}
              onChange={(event) => setBulkCategory(event.target.value)}
            >
              <option value={BULK_NO_CHANGE}>No change</option>
              {PAYMENT_CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <button type="button" className="rm-topBtn rm-topBtn--green posBulkApply" onClick={applyBulkEdit} disabled={!selectedCount || bulkSaving}>
            {bulkSaving ? "Applying" : "Apply"}
          </button>
        </div>
      </div>

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">POS payment catalog</h3>
          <div className="rm-rates-subtitle">Automatic methods from imported ticket payments</div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading payment methods..." : `Showing ${count ? offset + 1 : 0}-${Math.min(count, offset + methods.length)} of ${count}`}
          </small>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn" disabled={!hasPrevious} onClick={() => loadMethods(Math.max(0, offset - limit))}>
              Previous
            </button>
            <button type="button" className="rm-topBtn" disabled={!hasNext} onClick={() => loadMethods(offset + limit)}>
              Next
            </button>
          </div>
        </div>

        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table" style={{ "--rm-col1-width": "52px", "--rm-table-min-width": "880px" }}>
            <colgroup>
              <col style={{ width: "52px" }} />
              <col style={{ width: "280px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "110px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th">
                  <button type="button" className="res-thBtn posSelectionHeader" onClick={toggleVisibleSelection}>
                    {allFilteredSelected || visibleMethodIds.some((id) => selectedIds.has(id)) ? <FiCheckSquare /> : <FiSquare />}
                  </button>
                </th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Method</span><span className="res-thSub">Source name</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Category</span><span className="res-thSub">Tender type</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Usage</span><span className="res-thSub">Tickets</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Last seen</span><span className="res-thSub">Import activity</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Catalog</span></div></th>
              </tr>
            </thead>
            <tbody>
              {methods.map((method) => {
                const rowSelected = allFilteredSelected || selectedIds.has(method.id);
                return (
                  <tr key={method.id}>
                    <td className="posSelectionCell">
                      <input
                        type="checkbox"
                        className="posRowCheckbox"
                        checked={rowSelected}
                        disabled={allFilteredSelected}
                        onChange={() => toggleMethodSelection(method.id)}
                        aria-label={`Select ${method.name}`}
                      />
                    </td>
                    <td>
                      <strong className="posPaymentMethodName">{method.name}</strong>
                      <small className="posPaymentMethodCode">{method.normalized_name}</small>
                    </td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={method.category || "other"}
                        disabled={savingId === method.id}
                        onChange={(event) => updateMethod(method, { category: event.target.value })}
                      >
                        {PAYMENT_CATEGORY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>{Number(method.payments_count || 0)}</td>
                    <td>{formatDate(method.last_seen_at)}</td>
                    <td>
                      <span className={`rm-chip ${method.is_active ? "rm-chip--ok" : "rm-chip--off"}`}>
                        {method.is_active ? categoryLabel(method.category) : "Inactive"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!methods.length ? (
                <tr>
                  <td colSpan="6" className="posMuted">No payment methods found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
